// src/App.js
import React, { useContext } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation
} from 'react-router-dom';

import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import { ThemeProvider } from './context/ThemeContext';
import { SidebarProvider, SidebarContext } from './context/SidebarContext';
import { DialogProvider } from './context/DialogContext';
import { ConnectivityProvider } from './context/ConnectivityContext';
import OfflineBanner from './components/common/OfflineBanner';
import EditProfile from './pages/EditProfile';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import './setupAxios';

// Admin Components & Layout
import AdminLayout from './pages/Admin/AdminLayout';
import CandidateWorkStatusReport from './components/reports/CandidateWorkStatusReport';
import ScoringGuideAuditTrail from './pages/Admin/ScoringGuideAuditTrail';
import Dashboard from './pages/Admin/Dashboard';
import SeriesDefine from './pages/Admin/SeriesDefine';
import SeriesView from './pages/Admin/SeriesView';
import SeriesEdit from './pages/Admin/SeriesEdit';
import UsersCategory from './pages/Admin/UsersCategory';
import UsersCategoryView from './pages/Admin/UsersCategoryView';
import UsersCategoryEdit from './pages/Admin/UsersCategoryEdit';
import UsersCreate from './pages/Admin/UsersCreate';
import UsersView from './pages/Admin/UsersView';
import UsersEdit from './pages/Admin/UsersEdit';
import ProgramsCreate from './pages/Admin/ProgramsCreate';
import ProgramsView from './pages/Admin/ProgramsView';
import ProgramsEdit from './pages/Admin/ProgramsEdit';
import ProgramExplore from './pages/Admin/ProgramExplore';
import ProgramCategoriesCreate from './pages/Admin/ProgramCategoriesCreate';
import ProgramCategoriesView from './pages/Admin/ProgramCategoriesView';
import ProgramCategoriesEdit from './pages/Admin/ProgramCategoriesEdit';
import ModulesCreate from './pages/Admin/ModulesCreate';
import ModulesView from './pages/Admin/ModulesView';
import ModulesEdit from './pages/Admin/ModulesEdit';
import ModuleScoringGuide from './pages/Admin/ModuleScoringGuide';
import ExamTypesCreate from './pages/Admin/ExamTypesCreate';
import ExamTypesView from './pages/Admin/ExamTypesView';
import ExamTypesEdit from './pages/Admin/ExamTypesEdit';
import SemestersCreate from './pages/Admin/SemestersCreate';
import SemestersView from './pages/Admin/SemestersView';
import SemestersEdit from './pages/Admin/SemestersEdit';
import ExamsView from './pages/Admin/ExamsView';
import ExamUpdate from './pages/Admin/ExamUpdate';
import CreateExam from './pages/Admin/ScheduleExam';
import ExamCentresCreate from './pages/Admin/ExamCentresCreate';
import ExamCentresView from './pages/Admin/ExamCentresView';
import ExamCentresEdit from './pages/Admin/ExamCentresEdit';
import StudentCreate from './pages/Admin/StudentCreate';
import StudentView from './pages/Admin/StudentView';
import StudentEdit from './pages/Admin/Student.Edit';
import YearsCreate from './pages/Admin/YearsCreate';
import YearsView from './pages/Admin/YearsView';
import YearsEdit from './pages/Admin/YearsEdit';
import StudentProfile from './pages/Admin/StudentProfile';
import ApiGatewayTest from './pages/Admin/ApiGatewayTest';
import CategoryLevelFetchSelection from './pages/Admin/CategoryLevelFetchSelection';

// Examiner Components & Layout
import JobsDashboard from './pages/Examiner/JobsDashboard';
import ExaminerReports from './pages/Examiner/ExaminerReports';
import JobDetails from './pages/Examiner/JobDetails';
import ExaminerLayout from './pages/Examiner/ExaminerLayout';
import ExamUploadPage from './pages/Examiner/ExamUploadPage';
import ExaminerDashboard from './pages/Examiner/ExaminerDashboard';

// Archiver Components & Layout
import ArchiverLayout from './pages/Archiver/ArchiverLayout';
import ArchiverDashboard from './pages/Archiver/ArchiverDashboard';
import ActiveSeries from './pages/Archiver/ActiveSeries';
import AssignWork from './pages/Archiver/AssignWork';
import AllocateProgress from './pages/Archiver/AllocateProgress';
import WorkUploadedReport from './pages/Archiver/WorkUploadedReport';
import StudentsWorkDownload from './pages/Archiver/StudentsWorkDownload';
import ReviewUploads from './pages/Archiver/ReviewUploads';
import CompletedAssignmentsPage from './pages/Archiver/CompletedAssignmentsPage';
import ExamSeries from './pages/Archiver/ExamSeries';

// Data Team Components & Layout
import DataTeamLayout from './pages/DataTeam/DataTeamLayout';
import DataTeamDashboard from './pages/DataTeam/DataTeamDashboard';
import DataTeamSeriesWorkDownload from './pages/DataTeam/DataTeamSeriesWorkDownload';
import DataTeamCadCentreDownload from './pages/DataTeam/DataTeamCadCentreDownload';
import DataTeamDownloadPerStudent from './pages/DataTeam/DataTeamDownloadPerStudent';

// Security Components & Layout
import SecurityLayout from './pages/Security/SecurityLayout';
import SecurityDashboard from './pages/Security/SecurityDashboard';
import SecurityScoringGuideList from './pages/Security/SecurityScoringGuideList';

// Public
import Login from './pages/Login';
import DataTeamDeploymentList from './pages/DataTeam/DataTeamDeploymentList';
import ForgotPassword from './pages/ForgotPassword';
import ArchiverReviewPage from './pages/Archiver/ArchiverReviewPage';

function AppContent() {
  const location = useLocation();
  const isLoginPage = location.pathname === '/login' || location.pathname === '/';
  const { sidebarOpen } = useContext(SidebarContext);

  // Adjust main content: shift right when sidebar is open.
  const contentStyle = {
    flex: 1,
    marginTop: isLoginPage ? 0 : '50px',
    marginLeft: isLoginPage ? 0 : (sidebarOpen ? '260px' : '0px'),
    padding: '1rem',
    transition: 'margin-left 0.3s ease'
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {!isLoginPage && <OfflineBanner />}
      {!isLoginPage && <Header />}
      <div style={contentStyle}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/profile/edit" element={<ProtectedRoute><EditProfile /></ProtectedRoute>} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="series/define" element={<SeriesDefine />} />
            <Route path="series/view" element={<SeriesView />} />
            <Route path="series/edit/:id" element={<SeriesEdit />} />
            <Route path="exam-series/create" element={<SeriesDefine />} />
            <Route path="exam-series/edit/:id" element={<SeriesEdit />} />
            <Route path="exam-series/view" element={<SeriesView />} />
            <Route path="api-gateway/test" element={<ApiGatewayTest />} />
            <Route path="api-gateway/fetch-selections" element={<CategoryLevelFetchSelection />} />
            <Route path="users/category" element={<UsersCategory />} />
            <Route path="users/category/view" element={<UsersCategoryView />} />
            <Route path="users/category/edit/:id" element={<UsersCategoryEdit />} />
            <Route path="users/create" element={<UsersCreate />} />
            <Route path="users/view" element={<UsersView />} />
            <Route path="users/edit/:userId" element={<UsersEdit />} />
            <Route path="programs/create" element={<ProgramsCreate />} />
            <Route path="programs/view" element={<ProgramsView />} />
            <Route path="programs/edit/:id" element={<ProgramsEdit />} />
            <Route path="programs/explore/:id" element={<ProgramExplore />} />
            <Route path="program-categories/create" element={<ProgramCategoriesCreate />} />
            <Route path="program-categories/view" element={<ProgramCategoriesView />} />
            <Route path="program-categories/edit/:id" element={<ProgramCategoriesEdit />} />
            <Route path="modules/create" element={<ModulesCreate />} />
            <Route path="modules/view" element={<ModulesView />} />
            <Route path="modules/edit/:id" element={<ModulesEdit />} />
            <Route path="exam-types/create" element={<ExamTypesCreate />} />
            <Route path="exam-types/view" element={<ExamTypesView />} />
            <Route path="exam-types/edit/:id" element={<ExamTypesEdit />} />
            <Route path="semesters/create" element={<SemestersCreate />} />
            <Route path="semesters/view" element={<SemestersView />} />
            <Route path="semesters/edit/:id" element={<SemestersEdit />} />
            <Route path="exams/create" element={<CreateExam />} />
            <Route path="exams/update/:id" element={<ExamUpdate />} />            <Route path="exams/view" element={<ExamsView />} />
            <Route path="exam-centres/create" element={<ExamCentresCreate />} />
            <Route path="exam-centres/view" element={<ExamCentresView />} />
            <Route path="exam-centres/edit/:id" element={<ExamCentresEdit />} />
            <Route path="students/create" element={<StudentCreate />} />
            <Route path="students/edit/:id" element={<StudentEdit />} />
            <Route path="students/view" element={<StudentView />} />
            <Route path="students/profile/:id" element={<StudentProfile />} />
            <Route path="years/view" element={<YearsView />} />
            <Route path="years/create" element={<YearsCreate />} />
            <Route path="years-of-study/edit/:id" element={<YearsEdit />} />
            <Route path="reports/candidate-work-status" element={<CandidateWorkStatusReport />} />
            <Route path="reports/scoring-guide-audit" element={<ScoringGuideAuditTrail />} />
            <Route path="*" element={<div>Page not found</div>} />
          </Route>

          <Route
            path="/examiner/*"
            element={
              <ProtectedRoute>
                <ExaminerLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<ExaminerDashboard />} />
            <Route path="jobs" element={<JobsDashboard />} />
            <Route path="reports" element={<ExaminerReports />} />
            <Route path="job/:jobId" element={<JobDetails />} />
            <Route path="job/:id/upload" element={<ExamUploadPage />} />
          </Route>

          <Route
            path="/archiver/*"
            element={
              <ProtectedRoute>
                <ArchiverLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<ArchiverDashboard />} />
            <Route path="exam-series/*" element={<ExamSeries />} />
            <Route path="active-series/*" element={<ActiveSeries />} />
            <Route path="allocate/assign" element={<AssignWork />} />
            <Route path="allocate/progress" element={<AllocateProgress />} />
            <Route path="reports/work-uploaded" element={<WorkUploadedReport />} />
            <Route path="reports/students-work" element={<StudentsWorkDownload />} />
            <Route path="reports/candidate-work-status" element={<CandidateWorkStatusReport />} />
            <Route path="review" element={<ArchiverReviewPage/>}/>
            <Route path="completed" element={<CompletedAssignmentsPage />} />
          </Route>

          <Route
            path="/data-team/*"
            element={
              <ProtectedRoute>
                <DataTeamLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DataTeamDashboard />} />
            <Route path="osm/series/:seriesId" element={<DataTeamSeriesWorkDownload examType="OSM" />} />
            <Route path="rlp/series/:seriesId" element={<DataTeamSeriesWorkDownload examType="RLP" />} />
            <Route path="cad/centres/:seriesId" element={<DataTeamCadCentreDownload />} />
            <Route path="cad/candidates" element={<DataTeamDownloadPerStudent />} />
            <Route path="reports/deployment-list" element={<DataTeamDeploymentList />} />
            {/* Legacy link kept working in case it's bookmarked */}
            <Route path="download-per-student" element={<DataTeamDownloadPerStudent />} />
          </Route>

          <Route
            path="/security/*"
            element={
              <ProtectedRoute>
                <SecurityLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<SecurityDashboard />} />
            <Route path="scoring-guides" element={<SecurityScoringGuideList />} />
            <Route path="scoring-guides/:id" element={<ModuleScoringGuide canUpload={true} />} />
            <Route path="reports/scoring-guide-audit" element={<ScoringGuideAuditTrail />} />
            <Route path="osm/series/:seriesId" element={<DataTeamSeriesWorkDownload examType="OSM" />} />
            <Route path="rlp/series/:seriesId" element={<DataTeamSeriesWorkDownload examType="RLP" />} />
            <Route path="cad/centres/:seriesId" element={<DataTeamCadCentreDownload />} />
            <Route path="cad/candidates" element={<DataTeamDownloadPerStudent />} />
            <Route path="reports/deployment-list" element={<DataTeamDeploymentList />} />
          </Route>

          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="*" element={<h2>404 - Not Found</h2>} />
        </Routes>
      </div>
      {!isLoginPage && <Footer />}
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <ConnectivityProvider>
          <ThemeProvider>
            <SidebarProvider>
              <DialogProvider>
                <AppContent />
              </DialogProvider>
            </SidebarProvider>
          </ThemeProvider>
        </ConnectivityProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
